import type { ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import AppLayout from "@/components/layout/AppLayout";
import AuthLayout from "@/components/layout/AuthLayout";
import FullLayout from "@/components/layout/FullLayout";
import { ROUTES } from "@/constants/routes";
import PrivateRoute from "@/router/PrivateRoute";
import { RequirePermission } from "@/router/RequirePermission";
import {
  hidePermsForArea,
  hidePermsForPage as hidden,
  permsForArea,
  permsForPage as page,
} from "@/constants/nav-permissions";
import { routeFor } from "@/routes";

import BarangaysPage from "@/pages/BarangaysPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import ClubRecommendationPage from "@/pages/ClubRecommendationPage";
import CitiesPage from "@/pages/CitiesPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import ConfigurationPage from "@/pages/ConfigurationPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeeActivityLogPage from "@/pages/EmployeeActivityLogPage";
import EmployeesPage from "@/pages/EmployeesPage";
import HelpdeskPage from "@/pages/HelpdeskPage";
import HelpdeskTicketPage from "@/pages/HelpdeskTicketPage";
import LoginPage from "@/pages/LoginPage";
import MemberActivityLogPage from "@/pages/MemberActivityLogPage";
import MemberAnalyticsPage from "@/pages/MemberAnalyticsPage";
import MemberDetailPage from "@/pages/MemberDetailPage";
import MemberNewPage from "@/pages/MemberNewPage";
import MemberPopulationPage from "@/pages/MemberPopulationPage";
import MembersPage from "@/pages/MembersPage";
import NonMembersPage from "@/pages/NonMembersPage";
import ProfilePage from "@/pages/ProfilePage";
import ProfilePasswordPage from "@/pages/ProfilePasswordPage";
import ProvincesPage from "@/pages/ProvincesPage";
import ResetPasswordRequestPage from "@/pages/ResetPasswordRequestPage";
import RolesPage from "@/pages/RolesPage";
import SetPasswordPage from "@/pages/SetPasswordPage";
import YellowPageCategoriesPage from "@/pages/YellowPageCategoriesPage";
import YellowPagePage from "@/pages/YellowPagePage";

/**
 * Renders our own BrowserRouter only when asked to. When the host micro-
 * frontend shell already provides a Router, it passes isBrowserRouter and
 * we mount a fragment so useLocation resolves against the host router.
 */
const BrowserWrapper = ({
  isDisabledBrowserRouter,
  children,
}: {
  isDisabledBrowserRouter?: boolean | undefined;
  children: ReactNode;
}) => {
  if (!isDisabledBrowserRouter) return <BrowserRouter>{children}</BrowserRouter>;
  return <>{children}</>;
};

/** The catalog's stable uuid for a path, as the route `handle` the shell reads. */
const handle = (path: string) => ({ code: routeFor(path)?.code });

/**
 * Every address that is not everyone's is gated here as well as in the menu.
 *
 * Hiding a sidebar row is not a guard: the URL can be typed, bookmarked, or
 * followed from an old link. `nav-permissions.ts` states each rule once, so the
 * row and the route can never disagree about who may open a screen — and the
 * API refuses independently, so this only decides whether a person sees an
 * answer or a screenful of failed requests.
 */
const AppRouter = (props: { isBrowserRouter?: boolean | undefined }) => (
  <BrowserWrapper isDisabledBrowserRouter={props.isBrowserRouter}>
    <Routes>
      {/* Signed-out. The layout itself bounces anyone who already has a session. */}
      <Route element={<AuthLayout />}>
        <Route path={ROUTES.LOGIN} element={<LoginPage />} />
        <Route path={ROUTES.RESET_PASSWORD_REQUEST} element={<ResetPasswordRequestPage />} />
        <Route path={ROUTES.SET_PASSWORD} element={<SetPasswordPage />} />
      </Route>

      <Route element={<PrivateRoute />}>
        {/* Whole-screen signed-in pages: no rail, no header. */}
        <Route element={<FullLayout />}>
          <Route path={ROUTES.CHANGE_PASSWORD} element={<ChangePasswordPage />} />
          <Route
            path={ROUTES.MEMBER_NEW}
            element={
              <RequirePermission
                anyOf={[...page("members.new")]}
                noneOf={[...hidden("members.new")]}
              >
                <MemberNewPage />
              </RequirePermission>
            }
          />
        </Route>

        {/* Everything inside the shell. */}
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} handle={handle(ROUTES.DASHBOARD)} />
          <Route
            path={ROUTES.MEMBERS}
            element={
              <RequirePermission
                anyOf={[...permsForArea("members")]}
                noneOf={[...hidePermsForArea("members")]}
              >
                <MembersPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.MEMBERS)}
          />
          <Route
            path={ROUTES.MEMBER_DETAIL}
            element={
              <RequirePermission
                anyOf={[...page("members.detail")]}
                noneOf={[...hidden("members.detail")]}
              >
                <MemberDetailPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.MEMBER_DETAIL)}
          />
          <Route
            path={ROUTES.MEMBER_ANALYTICS}
            element={
              <RequirePermission
                anyOf={[...page("member-analytics")]}
                noneOf={[...hidden("member-analytics")]}
              >
                <MemberAnalyticsPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.MEMBER_ANALYTICS)}
          />
          <Route
            path={ROUTES.MEMBER_ACTIVITY_LOG}
            element={
              <RequirePermission
                anyOf={[...page("members.log")]}
                noneOf={[...hidden("members.log")]}
              >
                <MemberActivityLogPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.MEMBER_ACTIVITY_LOG)}
          />
          <Route
            path={ROUTES.MEMBER_POPULATION}
            element={
              <RequirePermission
                anyOf={[...page("member-population")]}
                noneOf={[...hidden("member-population")]}
              >
                <MemberPopulationPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.MEMBER_POPULATION)}
          />
          <Route
            path={ROUTES.CLUB_RECOMMENDATION}
            element={
              <RequirePermission
                anyOf={[...page("club-recommendation")]}
                noneOf={[...hidden("club-recommendation")]}
              >
                <ClubRecommendationPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.CLUB_RECOMMENDATION)}
          />
          <Route
            path={ROUTES.NON_MEMBERS}
            element={
              <RequirePermission
                anyOf={[...page("non-members")]}
                noneOf={[...hidden("non-members")]}
              >
                <NonMembersPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.NON_MEMBERS)}
          />
          {/* Supervisor and above for the list; the whole estate's history is
              a manager's, so the log asks for its own codename. */}
          <Route
            path={ROUTES.EMPLOYEES}
            element={
              <RequirePermission
                anyOf={[...page("employees.list")]}
                noneOf={[...hidden("employees.list")]}
              >
                <EmployeesPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.EMPLOYEES)}
          />
          <Route
            path={ROUTES.EMPLOYEE_ACTIVITY_LOG}
            element={
              <RequirePermission
                anyOf={[...page("employees.log")]}
                noneOf={[...hidden("employees.log")]}
              >
                <EmployeeActivityLogPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.EMPLOYEE_ACTIVITY_LOG)}
          />
          {/* The queue, and one ticket. Both ask for the same codename: a
              ticket is not a second kind of thing to be trusted with, and a
              link to one arrives from an email as often as from the list. */}
          <Route
            path={ROUTES.HELPDESK}
            element={
              <RequirePermission
                anyOf={[...permsForArea("helpdesk")]}
                noneOf={[...hidePermsForArea("helpdesk")]}
              >
                <HelpdeskPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.HELPDESK)}
          />
          <Route
            path={ROUTES.HELPDESK_TICKET}
            element={
              <RequirePermission
                anyOf={[...page("helpdesk.ticket")]}
                noneOf={[...hidden("helpdesk.ticket")]}
              >
                <HelpdeskTicketPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.HELPDESK_TICKET)}
          />
          <Route
            path={ROUTES.YELLOW_PAGE}
            element={
              <RequirePermission
                anyOf={[...page("yellow-page")]}
                noneOf={[...hidden("yellow-page")]}
              >
                <YellowPagePage />
              </RequirePermission>
            }
            handle={handle(ROUTES.YELLOW_PAGE)}
          />
          <Route
            path={ROUTES.YELLOW_PAGE_CATEGORIES}
            element={
              <RequirePermission
                anyOf={[...page("yellow-page-categories")]}
                noneOf={[...hidden("yellow-page-categories")]}
              >
                <YellowPageCategoriesPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.YELLOW_PAGE_CATEGORIES)}
          />
          <Route
            path={ROUTES.PROVINCES}
            element={
              <RequirePermission
                anyOf={[...page("provinces")]}
                noneOf={[...hidden("provinces")]}
              >
                <ProvincesPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.PROVINCES)}
          />
          <Route
            path={ROUTES.CITIES}
            element={
              <RequirePermission anyOf={[...page("cities")]} noneOf={[...hidden("cities")]}>
                <CitiesPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.CITIES)}
          />
          <Route
            path={ROUTES.BARANGAYS}
            element={
              <RequirePermission
                anyOf={[...page("barangays")]}
                noneOf={[...hidden("barangays")]}
              >
                <BarangaysPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.BARANGAYS)}
          />
          <Route
            path={ROUTES.CONFIGURATION}
            element={
              <RequirePermission
                anyOf={[...permsForArea("configuration")]}
                noneOf={[...hidePermsForArea("configuration")]}
              >
                <ConfigurationPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.CONFIGURATION)}
          />
          {/* Reached from the account menu rather than a sidebar row, and
              guarded exactly as a row would be. */}
          <Route
            path={ROUTES.ROLES}
            element={
              <RequirePermission
                anyOf={[...permsForArea("roles")]}
                noneOf={[...hidePermsForArea("roles")]}
              >
                <RolesPage />
              </RequirePermission>
            }
            handle={handle(ROUTES.ROLES)}
          />
          <Route
            path={ROUTES.PROFILE}
            element={<ProfilePage />}
            handle={handle(ROUTES.PROFILE)}
          />
          <Route
            path={ROUTES.PROFILE_PASSWORD}
            element={<ProfilePasswordPage />}
            handle={handle(ROUTES.PROFILE_PASSWORD)}
          />
          {/* Menu entries with no page yet — one slug each, 404 for anything else. */}
          <Route path={ROUTES.COMING_SOON} element={<ComingSoonPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  </BrowserWrapper>
);

export default AppRouter;
